import { Menu, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CardDeliveryModal } from '../../../components/CardDeliveryModal';
import CollapsibleFilters from '../../../components/CollapsibleFilters';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import ManagerCardPresetCard from '../../../components/ManagerCardPresetCard';
import SidebarMenu from '../../../components/SidebarMenu';
import api from '../../../service/api';
import type { Card as CardType, User } from '../../../service/api/types';
import { useManagerCards } from '../../../store/managerCards';

export default function CardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cards, isLoading, error, fetchCards, clearError } = useManagerCards();
  const [showSidebar, setShowSidebar] = useState(false);
  const [titleFilter, setTitleFilter] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CardType | null>(null);
  const [managedUsers, setManagedUsers] = useState<User[]>([]);

  useEffect(() => {
    fetchCards();
    api
      .usersGetAll()
      .then(setManagedUsers)
      .catch(() => {});
  }, [fetchCards]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleDeliver = (card: CardType) => {
    setSelectedCard(card);
    setShowDeliveryModal(true);
  };

  const filteredCards = titleFilter.trim()
    ? cards.filter(card =>
        (card.title || '')
          .toLowerCase()
          .includes(titleFilter.toLowerCase().trim()),
      )
    : cards;

  return (
    <CollapsibleFilters.Provider>
      <div className="bg-white min-vh-100 d-flex flex-column">
        <Container fluid className="flex-grow-1 pb-5">
          {/* Sticky Header */}
          <div
            className="sticky-top bg-white"
            style={{
              zIndex: 1020,
              borderBottom: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            {/* Title Bar */}
            <Row className="align-items-center p-4 pb-2">
              <Col xs="auto">
                <Menu
                  size={24}
                  className="text-dark"
                  onClick={() => setShowSidebar(true)}
                  style={{ cursor: 'pointer' }}
                />
              </Col>
              <Col className="d-flex flex-row align-items-center justify-content-center">
                <h2 className="text-dark h5 fw-bold mb-0 me-3">
                  {t('cards.title')}
                </h2>
                <CollapsibleFilters.Button />
              </Col>
              <Col xs="auto">
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-circle p-2"
                  onClick={() => navigate('/manager/create-card')}
                >
                  <Plus size={20} />
                </Button>
              </Col>
            </Row>

            {/* Collapsible Filters Content */}
            <CollapsibleFilters.Content>
              <Row className="align-items-center g-3">
                <Col xs={12} sm="auto">
                  <h6 className="mb-0 fw-bold">{t('cards.filters')}</h6>
                </Col>
                <Col xs={12} sm>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <Search size={16} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('cards.searchByTitle')}
                      value={titleFilter}
                      onChange={e => setTitleFilter(e.target.value)}
                      className="border-start-0"
                      style={{ fontSize: '14px' }}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </CollapsibleFilters.Content>
          </div>

          {/* Content */}
          <div className="px-0 pt-3">
            {isLoading && cards.length === 0 && (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {!isLoading && !error && filteredCards.length === 0 && (
              <div className="text-center py-5">
                <p className="text-muted mb-3">
                  {titleFilter.trim()
                    ? t('cards.noCardsMatchingSearch')
                    : t('cards.noCardPresetsYet')}
                </p>
                {!titleFilter.trim() && (
                  <Button
                    variant="primary"
                    onClick={() => navigate('/manager/create-card')}
                  >
                    <Plus size={16} className="me-1" />
                    {t('cards.createYourFirstCard')}
                  </Button>
                )}
              </div>
            )}

            {!isLoading &&
              !error &&
              filteredCards.map(card => (
                <ManagerCardPresetCard
                  key={card._id}
                  card={card}
                  onDeliver={handleDeliver}
                />
              ))}
          </div>
        </Container>

        <CardDeliveryModal
          show={showDeliveryModal}
          onHide={() => {
            setShowDeliveryModal(false);
            setSelectedCard(null);
          }}
          card={selectedCard}
          managedUsers={managedUsers}
        />
        <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
        <ManagerBottomNavigationBar />
      </div>
    </CollapsibleFilters.Provider>
  );
}
